import { toObjectTypeAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type {
	CmdbObjectResponse,
	CmdbObject,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import type { ObjectType as ObjectTypeResponse } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/object-type.tsx';
import { toCmdbObjectTypeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { ObjectType } from './object-types/types.tsx';

/**
 * =======================================
 * Context for Insight Object types in JFE
 * ---------------------------------------
 *
 * There are currently 3 different Insight Object shapes that get passed around
 * between the Insight Service, Jira Monolith and Jira Frontend. The methods in
 * this file help to get the various objects into a shape that the frontend is
 * expecting.
 *
 * Insight Service type - InsightServiceObject
 * Jira monolith type - CmdbObjectResponse
 * Jira frontend type - CmdbObject
 *
 * Examples:
 *
 * To transform a [Insight Service Object] to a [Jira Monolith Object] you would
 * pass each object to the `transformInsightServiceObjectToCmdbObjectResponse`
 * function
 *
 * To transform a [Jira Monolith Object] to a [Jira Frontend Object] you would
 * pass each object to the `transformBackendValueToCmdbObject` function
 *
 * To transform a [Insight Service Object] to a [Jira Frontend Object] you would
 * pass each object to `transformInsightServiceObjectToCmdbObjectResponse` and
 * then `transformBackendValueToCmdbObject`
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InsightServiceObject = any;

/**
 * Responses retrieved directly from the Insight service are slightly different
 * to the shape of objects retrieved through the Jira monolith. This function
 * attempts to normalise that difference.
 *
 * Note that because we don't have a properly typed input for this function,
 * it's extra careful when accessing nested properties using optional chaining.
 */
export const transformInsightServiceObjectToCmdbObjectResponse = (
	object: InsightServiceObject,
): CmdbObjectResponse => {
	const attributes =
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		object?.attributes?.map((attribute: any) => ({
			...attribute,
			objectAttributeValues:
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				attribute?.objectAttributeValues?.map((value: any) => {
					// Remaps the keys used for IDs slightly in 'object' type attributes
					if (attribute?.objectTypeAttribute?.type === 1) {
						return {
							...value,
							referencedObject: {
								...value?.referencedObject,
								objectId: value?.referencedObject?.id, // Actual object ID
								id: value?.referencedObject?.globalId, // Combined workspace ID and object ID
							},
						};
					}
					return value;
				}) ?? [],
		})) ?? [];
	return {
		...object,
		attributes,
	};
};

/**
 * Converts a CMDB object (as passed from the REST api) into an object with a
 * customised shape for the frontend.
 *
 * The difference with the FE shape is that it hoists up `objectTypeAttribute.type`
 * to the top of each attribute. This allows Flow to distinguish our disjoint union types.
 */
export const transformBackendValueToCmdbObject = (object: CmdbObjectResponse): CmdbObject => {
	const attributes = object.attributes.map((attribute) => {
		const castAttr = toObjectTypeAttribute(attribute);
		return {
			...castAttr,
			type: castAttr.objectTypeAttribute.type,
		};
	});
	return {
		...object,
		// @ts-expect-error - TS2322 - Type '({ type: 0 | 1 | 2 | 4 | 7; id: string; objectTypeAttributeId: string; objectTypeAttribute: CmdbObjectTypeAttributeDefault; objectAttributeValues: CmdbObjectAttributeValueDefault[]; } | { ...; } | { ...; } | { ...; } | { ...; })[]' is not assignable to type 'CmdbObjectAttribute[]'.
		attributes,
	};
};

/**
 * Sorts and reduces an array of object attributes into a shape that can be consumed
 * by the objecttype select which is used in the object form to retrieve attributes.
 */
export const reduceObjectTypesByParentObjectTypeId = (
	objectTypes: ObjectTypeResponse[],
): ObjectType[] =>
	objectTypes
		.sort((a, b) => (Number(a.id) > Number(b.id) ? 1 : -1))
		.reduce<ObjectType[]>(
			(entries, { id: objectTypeId, name, icon, position, parentObjectTypeId }) => {
				const objectType: ObjectType = {
					id: toCmdbObjectTypeId(objectTypeId),
					name,
					icon,
					position,
					parentObjectTypeId: parentObjectTypeId ?? null,
				};

				if (objectType.parentObjectTypeId == null) {
					entries.push(objectType);
					return entries;
				}

				const newEntries: Array<ObjectType> = [];
				const index = entries.findIndex(({ id }) => id === parentObjectTypeId);

				entries.slice(0, index + 1).forEach((entry) => {
					newEntries.push(entry);
				});
				newEntries.push(objectType);
				entries.slice(index + 1).forEach((entry) => {
					newEntries.push(entry);
				});

				return newEntries;
			},
			[],
		);
